import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  from,
} from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { gqlUrl } from 'lib/apiUrl';
import {
  getAuthDataFromCookies,
  getAuthDataFromSessionStorage,
} from 'src/utils/authUtils';

let logout: () => void = () => {};

export const setLogoutHandler = (logoutHandler: () => void) => {
  logout = logoutHandler;
};

const httpLink = createHttpLink({
  uri: gqlUrl,
  credentials: 'include',
});

const retryLink = new RetryLink({
  attempts: {
    max: 2,
    retryIf: (error, _operation) => {
      console.warn('[APOLLO RETRY] Retrying due to error:', error);
      if (error.statusCode === 401) {
        console.warn('[APOLLO RETRY] Unauthorized (401) — triggering logout');
        logout();
        return true;
      }
      return false;
    },
  },
});

const authLink = setContext((_, { headers }) => {
  const session = getAuthDataFromSessionStorage();
  const cookies = getAuthDataFromCookies();
  const { jwt } = session.jwt ? session : cookies;
  console.log(
    '[APOLLO AUTH] Using JWT from:',
    session.jwt ? 'sessionStorage' : 'cookies'
  );
  return {
    headers: {
      ...headers,
      authorization: jwt ? `Bearer ${jwt}` : '',
    },
  };
});

// Custom logger link for timing and debugging
const loggerLink = new ApolloLink((operation, forward) => {
  const start = performance.now();
  const operationName = operation.operationName;
  const variables = operation.variables;

  console.log(`📡 [APOLLO] Starting operation: ${operationName}`);
  console.log(`📦 [APOLLO] Variables:`, variables);

  return forward(operation).map((response) => {
    const duration = performance.now() - start;
    console.log(
      `✅ [APOLLO] Completed: ${operationName} in ${duration.toFixed(2)}ms`
    );
    return response;
  });
});

const errorLoggerLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response?.errors?.length) {
      console.error('[APOLLO ERROR] GraphQL Errors:', response.errors);
    }
    return response;
  });
});

// Final Apollo Client
export const client = new ApolloClient({
  link: from([retryLink, authLink, loggerLink, errorLoggerLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
      context: {},
    },
  },
});

// TODO: we need to add the following code to the client.ts file in the apolloClient folder, but i can break a lot of logic in the app, so we need to test it first
// export const client = new ApolloClient({
//   link: retryLink.concat(authLink).concat(httpLink),
//   cache: new InMemoryCache({
//     resultCaching: false, Setting resultCaching: false tells Apollo not to cache the results of your queries, which can be useful if you want every network request to be fresh. This is particularly useful for queries that return data that changes frequently, like stock prices or weather data.
//   }),
//   defaultOptions: {
//     watchQuery: {
//       fetchPolicy: 'no-cache', When you create your client, you can specify that both regular queries and watched queries (which include fetchMore) use a fetch policy of 'no-cache'. For example:
//     },
//     query: {
//       fetchPolicy: 'no-cache',
//     },
//   },
// });
